import React, { useCallback, useMemo } from 'react'
import { useNewTranslation } from 'hooks'
import { BaseEditorContainer } from '../PredefinedTaskEditor/styles'
import { Header } from '../Shared'
import styled from 'particles/themes/styled'
import Text from 'particles/Text'
import { theme } from 'theme'
import { AddBlockButton as BaseAddBlockButton, BlockEditor } from '../Shared/Blocks'
import { InputFieldContainer } from 'components/Shared/Fields/styles'
import { BLOCK_TYPE } from 'state/registrations'
import { BLOCK_TYPE as CUSTOM_TASK_BLOCK_TYPE } from 'state/tasks'
import { useField, useFormikContext } from 'formik'
import { BlockField, EditorValues } from '.'
import { generateUuid } from 'services/uuid'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import { reorderList } from 'components/utils'
import _ from 'lodash'
import { BLOCK_GROUP } from '../Shared/Blocks/BlockMenu'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import { useSelector } from 'react-redux'
import { CAMPAIGN_STATUS_ENUM, selectEditingCampaign } from 'state/campaigns'
import FieldTitleAndSubTitle from 'components/Shared/Fields/FieldTitleAndSubTitle'
import { fieldIdentifiers } from 'components/Shared/Comment'
import Toggle from 'particles/Toggle'

const BlocksContainer = styled(InputFieldContainer)`
    max-width: 594px;

    & .blocks-area {
        display: flex;
        flex-direction: column;
        margin-bottom: ${theme.newSpacing.s};

        // Block Editor Container
        & > div {
            margin-bottom: ${theme.newSpacing.s};
        }
    }
`

interface RegistrationFormBuilderProps {
    className?: string
}

function createNewBlock(type: BLOCK_TYPE): BlockField {
    let initialValue = {
        uuid: generateUuid(),
        type,
        title: '',
        description: '',
        is_required: true,
        data: {},
        images: [],
        options: [],
    } as BlockField
    switch (type) {
        case BLOCK_TYPE.TEXT:
            initialValue.is_required = false
            break
        case BLOCK_TYPE.PHOTOS:
            initialValue.is_required = false
            break
        case BLOCK_TYPE.QUESTION:
            initialValue = {
                ...initialValue,
                data: {
                    isMultiAnswer: false,
                },
                hasOthers: false,
                options: [
                    { option: '', is_others: false },
                    { option: '', is_others: false },
                ],
                existingOtherOption: undefined,
            }
            break
        case BLOCK_TYPE.PHOTO_SUBMISSION:
            initialValue.data = {
                number_of_photos: 3,
            }
            break
        case BLOCK_TYPE.TEXT_SUBMISSION:
            initialValue.data = {
                is_long_answer: false,
            }
            break
    }
    return initialValue
}

interface AddBlockButtonProps {
    canEditCertainFields: boolean
}

const AddBlockButton: React.FC<AddBlockButtonProps> = ({ canEditCertainFields }) => {
    const { setValues, values } = useFormikContext<EditorValues>()
    const handleAddBlock = useCallback(
        (_blockType: CUSTOM_TASK_BLOCK_TYPE | BLOCK_TYPE): void => {
            const newBlock = createNewBlock(_blockType as BLOCK_TYPE)
            setValues({
                ...values,
                blocksData: { ...values.blocksData, [newBlock.uuid]: newBlock },
                blocksSorting: values.blocksSorting.concat([newBlock.uuid]),
            })
        },
        [values.blocksData, values.blocksSorting],
    )

    return (
        <BaseAddBlockButton
            onSelected={handleAddBlock}
            blockGroup={BLOCK_GROUP.REGISTRATION}
            showNonSubmissionBlockOnly={canEditCertainFields}
        />
    )
}

const RegistrationFormBuilder: React.FC<RegistrationFormBuilderProps> = ({ className }) => {
    const [t] = useNewTranslation()
    const { values, setFieldValue, setValues } = useFormikContext<EditorValues>()
    const [, meta] = useField<any>('blocksSorting')
    const showError = meta.touched && Boolean(meta.error)
    const campaign = useSelector(selectEditingCampaign)
    const isCampaignLive = Boolean(campaign && campaign.status === CAMPAIGN_STATUS_ENUM.LIVE)

    const onDragEnd = useCallback(
        (result: DropResult): void => {
            if (!result.destination) return
            const current = result.source.index
            const destination = result.destination.index

            const newValues = reorderList([...values.blocksSorting], current, destination)
            setFieldValue('blocksSorting', newValues)
        },
        [values.blocksSorting],
    )

    const handleRemoveBlock = useCallback(
        (blockIndex: number): void => {
            const toRemoveBlockUuid = values.blocksSorting[blockIndex]
            const newSorting = [...values.blocksSorting]
            const newData = { ...values.blocksData }
            delete newData[toRemoveBlockUuid]
            newSorting.splice(blockIndex, 1)
            setValues({
                ...values,
                blocksData: newData,
                blocksSorting: newSorting,
            })
        },
        [values.blocksData, values.blocksSorting],
    )

    const blocks = useMemo(
        () => values && values.blocksSorting && values.blocksSorting.map((uuid: string) => values.blocksData[uuid]),
        [values.blocksData, values.blocksSorting],
    )
    return (
        <CampaignEditorInputFieldContainer>
            {(isReadOnly: boolean) => {
                // If campaign is editable and live, some of the interest registration field should not be updated
                const canEditCertainFields = !isReadOnly && isCampaignLive
                return (
                    <BaseEditorContainer className={className}>
                        <Header title={t('campaignEditor.createTask.createRegistrationFormPageTitle')} />
                        <BlocksContainer>
                            <FieldTitleAndSubTitle
                                title={t('campaignEditor.createTask.selectRegistrationFieldsHeader')}
                                isRequired
                                identifier={fieldIdentifiers.registrationFieldsHeader}
                            />
                            {!isReadOnly ? (
                                <Text scale="bodyS400" color="secondary" className="field-sub-title">
                                    {t('campaignEditor.createTask.selectRegistrationFieldsSubtext')}
                                </Text>
                            ) : null}
                            <Toggle
                                isChecked={values.enable_collect_follower_count ?? true}
                                handleChange={() => {
                                    const updateValue = !Boolean(values.enable_collect_follower_count)
                                    setFieldValue('enable_collect_follower_count', updateValue)
                                }}
                                isReadOnly={campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE}
                                label={t('Registration.CollectFollwer.Title')}
                                subtext={t('registration.CollectFollower.Subtext')}
                                identifier={fieldIdentifiers.registrationCollectFollowerCount}
                            />

                            {!_.isEmpty(blocks) ? (
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId="droppable">
                                        {(droppableProvided): React.ReactElement => (
                                            <div
                                                className="blocks-area"
                                                ref={droppableProvided.innerRef}
                                                {...droppableProvided.droppableProps}
                                            >
                                                {blocks.map((block, index: number) => (
                                                    <Draggable key={block.uuid} draggableId={block.uuid} index={index}>
                                                        {(draggableProvided): React.ReactElement => (
                                                            <BlockEditor
                                                                type={block.type}
                                                                namePrefix={`blocksData.${block.uuid}`}
                                                                blockIndex={index}
                                                                onRemove={handleRemoveBlock}
                                                                ref={draggableProvided.innerRef}
                                                                draggableProvided={draggableProvided}
                                                                isReadOnly={isReadOnly}
                                                                canEditCertainFields={canEditCertainFields}
                                                                blockUuid={block.uuid}
                                                                {...draggableProvided.draggableProps}
                                                            />
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {droppableProvided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            ) : null}
                            <div>
                                {!isReadOnly ? <AddBlockButton canEditCertainFields={canEditCertainFields} /> : null}
                                <div className="error">{showError && meta.error}</div>
                            </div>
                        </BlocksContainer>
                    </BaseEditorContainer>
                )
            }}
        </CampaignEditorInputFieldContainer>
    )
}

export default styled(RegistrationFormBuilder)`
    min-height: 100%;
    height: fit-content;
`
